"use client";

import { useState } from "react";
import { X, Loader2, ArrowRight, ShieldCheck, Landmark, Layers, Wallet } from "lucide-react";
import { BROKING_SEGMENTS, segmentMeta, type BrokingLead } from "@/lib/demos/stockBrokingData";

interface StockBrokingStageModalProps {
  lead: BrokingLead;
  targetStage: string;
  onClose: () => void;
  onConfirm: (updates: Partial<BrokingLead>, note: string) => void;
}

export function StockBrokingStageModal({ lead, targetStage, onClose, onConfirm }: StockBrokingStageModalProps) {
  const isActivate = targetStage === "Account Activated";
  const isSegment = targetStage === "Segment Activation";
  const isTrader = targetStage === "Active Trader";

  const [dematNo, setDematNo] = useState(lead.demat_no || `IN300214 · ${Math.floor(10000000 + Math.random() * 89999999)}`);
  const [segments, setSegments] = useState<string[]>(
    lead.segments_requested.length ? lead.segments_requested : ["equity_delivery"]
  );
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [funding, setFunding] = useState(50000);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const needsRisk = isSegment && segments.some((s) => segmentMeta(s)?.requiresProof);

  function toggleSegment(id: string) {
    setSegments((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (needsRisk && !riskAccepted) return;
    setSubmitting(true);

    const updates: Partial<BrokingLead> = { stage: targetStage as BrokingLead["stage"] };

    if (isActivate) {
      updates.demat_no = dematNo;
      updates.kyc_status = "Verified";
      updates.esign_done = true;
      updates.bank_verified = true;
      updates.nominee_added = true;
      if (lead.segments_active.length === 0) updates.segments_active = ["equity_delivery", "mf_sip"];
    }
    if (isSegment) {
      updates.segments_active = Array.from(new Set([...lead.segments_active, ...segments]));
      updates.segments_requested = [];
    }
    if (isTrader) {
      updates.disposition = "funded";
      updates.margin_available = Math.round(Number(funding) * 4);
      updates.aum = Math.max(lead.aum, Number(funding));
      updates.brokerage_mtd = lead.brokerage_mtd || Math.round(Number(funding) * 0.004);
    }

    const autoNote =
      notes ||
      (isActivate
        ? `Account activated. UCC mapped at NSE & BSE. Demat ${dematNo}.`
        : isSegment
        ? `Segments activated: ${segments.map((s) => segmentMeta(s)?.short).join(", ")}.`
        : `First funding of ₹${Number(funding).toLocaleString("en-IN")} received; client is now an Active Trader.`);

    // Small delay to simulate exchange/depository round-trip.
    setTimeout(() => {
      onConfirm(updates, autoNote);
      setSubmitting(false);
      onClose();
    }, 650);
  }

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-2xl bg-white border border-slate-200 p-6 sm:p-7 shadow-2xl shadow-emerald-950/40 my-8">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-500 hover:text-slate-900 p-1 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-5">
          <span className="text-[10px] font-mono text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
            {lead.client_code}
          </span>
          <h3 className="text-lg font-bold text-slate-900 mt-2">
            Advance to <span className="text-emerald-600">{targetStage}</span>
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Client: <strong>{lead.name}</strong> · {lead.city}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ACCOUNT ACTIVATED */}
          {isActivate && (
            <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-blue-500/25">
              <div className="flex items-center gap-2 text-xs font-bold text-blue-700">
                <Landmark className="w-4 h-4" /> Depository Account Activation
              </div>
              <p className="text-[11px] text-slate-500">
                Confirms KYC, e-Sign and bank verification, allots the NSDL demat (DP·BO) id and maps the UCC at NSE &amp; BSE.
              </p>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">NSDL Demat (DP · BO ID) *</label>
                <input
                  required
                  value={dematNo}
                  onChange={(e) => setDematNo(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 text-xs font-mono focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* SEGMENT ACTIVATION */}
          {isSegment && (
            <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-violet-500/25">
              <div className="flex items-center gap-2 text-xs font-bold text-violet-700">
                <Layers className="w-4 h-4" /> Enable Trading Segments
              </div>
              <p className="text-[11px] text-slate-500">
                Segments marked with a proof badge require income proof per SEBI/exchange norms.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {BROKING_SEGMENTS.map((seg) => {
                  const on = segments.includes(seg.id);
                  const already = lead.segments_active.includes(seg.id);
                  return (
                    <button
                      key={seg.id}
                      type="button"
                      disabled={already}
                      onClick={() => toggleSegment(seg.id)}
                      className={`p-2.5 rounded-lg text-left border text-[11px] transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                        on || already ? "bg-violet-500/15 border-violet-500 text-slate-900" : "bg-white border-slate-300 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <div className="font-bold flex items-center justify-between">
                        {seg.short}
                        {seg.requiresProof && <span className="text-[8px] text-amber-700">proof</span>}
                      </div>
                      <div className="text-[9px] text-slate-500 mt-0.5">{seg.exchange}</div>
                    </button>
                  );
                })}
              </div>
              {needsRisk && (
                <label className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={riskAccepted}
                    onChange={(e) => setRiskAccepted(e.target.checked)}
                    className="mt-0.5 accent-violet-500"
                  />
                  <span>Client has acknowledged the derivatives / MTF <strong>risk disclosure document</strong> and income proof is verified.</span>
                </label>
              )}
            </div>
          )}

          {/* ACTIVE TRADER */}
          {isTrader && (
            <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-emerald-500/25">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-700">
                <Wallet className="w-4 h-4" /> First Funding &amp; Activation
              </div>
              <p className="text-[11px] text-slate-500">
                Records the first funds transfer. Available margin is modelled at ~4× for eligible segments.
              </p>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Initial Funding Amount (₹) *</label>
                <input
                  type="number"
                  required
                  min={1000}
                  value={funding}
                  onChange={(e) => setFunding(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">RM Note (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add context — appended to the client's activity history…"
              className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-500"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || (needsRisk && !riskAccepted)}
            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Syncing with depository…</>
            ) : (
              <>Advance stage &amp; log activity <ArrowRight className="w-4 h-4" /></>
            )}
          </button>

          <div className="text-center text-[10px] text-slate-500 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Demo automation — WhatsApp confirmation to {lead.phone}
          </div>
        </form>
      </div>
    </div>
  );
}
